import React, { useEffect, useState } from 'react';
import styles from './style.module.css';
import { BotiqueLayoutImage, BoutiqueLayoutOverlay } from '../../assets/webp';
import { StoreAIBot } from '../../components/StoreAIBot';
import Typography from '../../components/Typography/Typography';
import { StoreArrowRight, StoreCart, StoreImgName, StoreSearchIconDark } from '../../assets/svgs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { StorePreviewModal } from '../../components/StorePreviewModal';
import AnimatedButton from '../../components/AnimatedButton';
import { useGetTemplateByIdQuery } from '../../redux/services/storesApi';

interface BoutiqueLayoutProps {
  backgroundImage?: string;
  overlayImage?: string;
}

export const BoutiqueLayout: React.FC<BoutiqueLayoutProps> = ({
  backgroundImage = BotiqueLayoutImage,
  overlayImage = BoutiqueLayoutOverlay
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { templateId } = useParams<{ templateId: string }>();
  const storeDetails = useSelector((state: RootState) => state.CreateStore);
  const navigate = useNavigate();

  const { data: templateData, isLoading, isError } = useGetTemplateByIdQuery(
    templateId || '',
    { skip: !templateId }
  );

  useEffect(() => {
    if (storeDetails) {
      console.log("Store details object:", storeDetails);
      console.log("Store Template:", storeDetails.storeTemplate);
    } else {
      console.log("No store details found in Redux");
    }
  }, [storeDetails]);

  useEffect(() => {
    if (templateData) {
      console.log("Template data loaded:", templateData);
    }
  }, [templateData]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handlePreviewStore = () => {
    if (isMobile) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const currentBackgroundImage = templateData?.image_preview_url || backgroundImage;
  const currentOverlayImage = templateData?.model_url || overlayImage;
  const templateName = templateData?.name || 'Boutique Store';
  const templateDescription = templateData?.description || '12-product Layout';
  const currentTemplateId = templateId || templateData?._id || '';

  // Extract layout details from API response
  const layoutDetails = templateData?.layout_details || {};
  const totalSize = layoutDetails?.total_size || '12m x 8m';
  const theme = layoutDetails?.theme || 'Indoor / White walls /Display case';
  const bestFor = layoutDetails?.best_for || 'Clothings, Apparel';
  const walkthroughLength = layoutDetails?.walkthrough_length || 'Medium (4 zones)';
  const maxProducts = layoutDetails?.max_products || 12;
  const minRecommended = layoutDetails?.min_recommended || 6;
  const maxFileSize = layoutDetails?.max_file_size || '20MB';
  const supportedFormats = layoutDetails?.supported_formats || 'glTF, FBX, ViewF Image-to-3D';
  const entryPosition = layoutDetails?.entry_position || 'center front (Position E1)';
  const movement = layoutDetails?.movement || 'Free roam or Step-by-step teleport';
  const defaultFacing = layoutDetails?.default_facing || 'A1 to A4';

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.boutiqueContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.loadingContainer}>
            <Typography
              variant='bodyLarge'
              fontFamily='satoshi'
              weight='regular'
              className={styles.loadingText}
            >
              Loading template details...
            </Typography>
          </div>
        </div>
        <StoreAIBot />
      </div>
    );
  }

  // Show error state
  if (isError && !templateData) {
    return (
      <div className={styles.boutiqueContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.errorContainer}>
            <Typography
              variant='bodyLarge'
              fontFamily='satoshi'
              weight='regular'
              className={styles.errorText}
            >
              Failed to load template details. Showing default layout.
            </Typography>
          </div>
        </div>
        <StoreAIBot />
      </div>
    );
  }

  return (
    <div className={styles.boutiqueContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.breadcrumbSection}>
          <Typography
            variant='bodyRegular'
            fontFamily='satoshi'
            weight='regular'
            className={styles.breadcrumb}
          >
            <span className={styles.breadcrumbContainer}>
              <Link
                to='/choose-template'
                className={styles.templatesLink}
              >
                Templates
              </Link>
              <img
                src={StoreArrowRight}
                alt='chevron right icon'
                className={styles.rightIcon}
              />
              <span className={styles.currentPage}>{templateName}</span>
            </span>
          </Typography>
        </div>

        <div className={styles.header}>
          <Typography
            variant='heading5'
            fontFamily='satoshi'
            weight='regular'
            className={styles.title}
          >
            {templateName} | {templateDescription}
          </Typography>

          {isMobile && (
            <div className={styles.headerActions}>
              <AnimatedButton
                variant='secondary'
                onClick={handlePreviewStore}
                className={styles.previewStoreBtn}
              >
                <img
                  src={StoreImgName}
                  alt="Welcome Name Icon"
                  className={styles.icon}
                />
                <Typography
                  variant='buttonText'
                  fontFamily='satoshi'
                  weight='regular'
                  size='base'
                  className={styles.previewStoreBtnText}
                >
                  Preview Store
                </Typography>
              </AnimatedButton>
            </div>
          )}
        </div>

        <div className={styles.mainContent}>
          <div className={styles.contentGrid}>
            <div className={styles.leftPanel}>
              <div className={styles.section}>
                <Typography
                  variant='heading3'
                  fontFamily='satoshi'
                  weight='bold'
                  className={styles.sectionTitle}>
                  Layout Details
                </Typography>

                <Typography
                  variant='bodyRegular'
                  fontFamily='satoshi'
                  weight='regular'
                  className={styles.detailsList}>
                  <ul>
                    <li>Total Layout Size: {totalSize}</li>
                    <li>Theme: {theme}</li>
                    <li>Best For: {bestFor}</li>
                    <li>Walkthrough Length: {walkthroughLength}</li>
                  </ul>
                </Typography>
              </div>

              <div className={styles.section}>
                <Typography
                  variant='heading3'
                  fontFamily='satoshi'
                  weight='bold'
                  className={styles.sectionTitle}>
                  Layout Capacity
                </Typography>
                <Typography
                  variant='bodyRegular'
                  fontFamily='satoshi'
                  weight='regular'
                  className={styles.detailsList}>
                  <ul>
                    <li>Max Products: {maxProducts}</li>
                    <li>Min Recommended: {minRecommended}</li>
                    <li>Max File Size (per product): {maxFileSize}</li>
                    <li>Supported Formats: {supportedFormats}</li>
                  </ul>
                </Typography>
              </div>

              <div className={styles.section}>
                <Typography
                  variant='heading3'
                  fontFamily='satoshi'
                  weight='bold'
                  className={styles.sectionTitle}>
                  Entry & Navigation Info
                </Typography>
                <Typography
                  variant='bodyRegular'
                  fontFamily='satoshi'
                  weight='regular'
                  className={styles.detailsList}>
                  <ul>
                    <li>Visitors enter from the {entryPosition}</li>
                    <li>Movement: {movement}</li>
                    <li>Default facing direction: {defaultFacing}</li>
                  </ul>
                </Typography>
              </div>

              <div className={styles.section}>
                <Typography
                  variant='heading3'
                  fontFamily='satoshi'
                  weight='bold'
                  className={styles.sectionTitle}>
                  Customization Features
                </Typography>

                <Typography
                  variant='bodyRegular'
                  fontFamily='satoshi'
                  weight='regular'
                  className={styles.detailsList}>
                  <ul>
                    <li>Supports prop placement</li>
                    <li>Editable wall color / background</li>
                    <li>Lighting presets</li>
                    <li>Drag and drop product repositioning</li>
                  </ul>
                </Typography>
              </div>

              {isMobile && (
                <div className={styles.mobileButtons}>
                  <AnimatedButton
                    variant='secondary'
                    onClick={handlePreviewStore}
                    className={styles.previewDemoBtn}
                  >
                    <Typography
                      variant='buttonText'
                      fontFamily='satoshi'
                      weight='regular'
                      size='base'
                      className={styles.previewDemoBtnText}
                    >
                      Preview Demo Store
                    </Typography>
                  </AnimatedButton>
                </div>
              )}
            </div>

            {!isMobile && (
              <div className={styles.rightPanel}>
                <div className={styles.storeContainer}>
                  <div className={styles.topNav}>
                    <FlatButtons className={styles.storeName}>
                      <img
                        src={StoreImgName}
                        alt="Welcome Name Icon"
                        className={styles.icon}
                      />
                      <Typography
                        variant='buttonText'
                        fontFamily='satoshi'
                        weight='regular'
                        size='base'
                        className={styles.storeNameText}>
                        {templateName}
                      </Typography>
                    </FlatButtons>
                    <FlatButtons className={styles.navIcons}>
                      <img
                        src={StoreSearchIconDark}
                        alt="Search Icon"
                        className={styles.icon}
                      />
                      <img
                        src={StoreCart}
                        alt="Cart Icon"
                        className={styles.icon}
                      />
                    </FlatButtons>
                  </div>

                  <div className={styles.storeLayout}>
                    <img
                      src={currentBackgroundImage}
                      alt="Store background"
                      className={styles.backgroundImage}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = backgroundImage;
                      }}
                    />

                    {currentOverlayImage && (
                      <div className={styles.overlay}>
                        <img
                          src={currentOverlayImage}
                          alt="Store overlay"
                          className={styles.overlayImage}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = overlayImage;
                          }}
                        />
                      </div>
                    )}

                    <FlatButtons className={styles.entryPoint}>
                      <Typography
                        variant='buttonText'
                        fontFamily='satoshi'
                        weight='regular'
                        size='base'
                        className={styles.storeNameText}>
                        Entry Point | E1
                      </Typography>
                    </FlatButtons>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.actionSection}>
            <AnimatedButton
              variant='primary'
              onClick={() => {
                navigate(`/add-info/${currentTemplateId}`)
              }}
              className={styles.useLayoutBtn}>
              <Typography
                variant='buttonText'
                fontFamily='satoshi'
                weight='regular'
                size='base'
                className={styles.useLayoutBtnText}>
                Use This Layout
              </Typography>
            </AnimatedButton>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <StorePreviewModal
          isOpen={isModalOpen}
          onClose={closeModal}
          backgroundImage={currentBackgroundImage}
          overlayImage={currentOverlayImage}
          storeName={templateName}
        />
      )}

      <StoreAIBot />
    </div>
  );
};